import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity } from "lucide-react";
import { api } from "../api";

const GENERIC_MESSAGE =
  "Se l'indirizzo è associato a un account attivo, riceverai un'email con le istruzioni per reimpostare la password.";

function RecoveryLayout({ title, description, children }) {
  return (
    <main className="login-shell">
      <section className="login-panel">
        <div className="brand-mark">
          <Activity size={25} />
          <span>CENTRALE <b>118</b></span>
        </div>

        <div className="login-copy">
          <span className="eyebrow">SICUREZZA ACCOUNT</span>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>

        {children}
      </section>

      <aside className="login-aside">
        <div className="signal-grid">
          <div>
            <span>01</span>
            <b>PROTEZIONE</b>
            <small>Collegamento personale e temporaneo</small>
          </div>
          <div>
            <span>02</span>
            <b>SICUREZZA</b>
            <small>Validità del collegamento: 15 minuti</small>
          </div>
          <div>
            <span>03</span>
            <b>ACCESSO</b>
            <small>Le sessioni precedenti saranno revocate</small>
          </div>
        </div>
        <div className="aside-foot">
          <span>● ACCESSO PROTETTO</span>
          <span>118 OPS</span>
        </div>
      </aside>
    </main>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState("");

  const submit = async event => {
    event.preventDefault();
    if (loading) return;

    setError("");
    setLoading(true);

    try {
      await api.post("/auth/forgot-password", {
        email: email.trim()
      });
      setCompleted(true);
    } catch (err) {
      setError(
        err.response?.status === 400
          ? "Inserisci un indirizzo email valido."
          : "Impossibile elaborare la richiesta. Riprova più tardi."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <RecoveryLayout
      title="Password dimenticata?"
      description="Inserisci l'indirizzo email associato al tuo account 118Finder. Se l'account è attivo, riceverai un collegamento per impostare una nuova password."
    >
      {completed ? (
        <div className="login-form">
          <div className="success" role="status">
            {GENERIC_MESSAGE}
          </div>
          <Link to="/" className="primary-btn">
            Torna al login
          </Link>
        </div>
      ) : (
        <form className="login-form" onSubmit={submit}>
          <label>
            Indirizzo email
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              maxLength={254}
              required
              data-testid="forgot-password-email-input"
            />
          </label>

          {error && <div className="error" role="alert">{error}</div>}

          <button
            className="primary-btn"
            type="submit"
            disabled={loading}
            data-testid="forgot-password-submit-button"
          >
            {loading ? "Invio in corso…" : "Invia link di ripristino"}
          </button>

          <Link to="/">Torna al login</Link>
        </form>
      )}
    </RecoveryLayout>
  );
}

export function ResetPassword() {
  const [token] = useState(
    () => new URLSearchParams(window.location.search).get("token") || ""
  );

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (token) {
      window.history.replaceState(
        window.history.state,
        "",
        window.location.pathname
      );
    }
  }, [token]);

  const submit = async event => {
    event.preventDefault();
    if (loading) return;

    setError("");

    if (!token || token.length > 256) {
      setError("Collegamento non valido o scaduto.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Le password inserite non coincidono.");
      return;
    }

    const bytes = new TextEncoder().encode(password).length;

    if (password.length < 8 || bytes > 72) {
      setError(
        "La password deve contenere almeno 8 caratteri e non superare 72 byte."
      );
      return;
    }

    setLoading(true);

    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: password
      });

      setCompleted(true);
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(
        err.response?.data?.detail ||
        "Impossibile reimpostare la password."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <RecoveryLayout
      title="Reimposta password"
      description="Scegli una nuova password personale per il tuo account 118Finder."
    >
      {completed ? (
        <div className="login-form">
          <div className="success" role="status">
            Password aggiornata correttamente.
            Ora puoi effettuare l'accesso.
          </div>

          <Link to="/" className="primary-btn">
            Vai al login
          </Link>
        </div>
      ) : (
        <form className="login-form" onSubmit={submit}>
          {!token && (
            <div className="error" role="alert">
              Collegamento mancante o non valido.
            </div>
          )}

          <label>
            Nuova password
            <input
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              value={password}
              onChange={e => setPassword(e.target.value)}
              data-testid="reset-password-new-input"
            />
          </label>

          <label>
            Conferma nuova password
            <input
              type="password"
              autoComplete="new-password"
              minLength={8}
              required
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              data-testid="reset-password-confirm-input"
            />
          </label>

          {error && <div className="error" role="alert">{error}</div>}

          <button
            className="primary-btn"
            type="submit"
            disabled={loading || !token}
            data-testid="reset-password-submit-button"
          >
            {loading ? "Salvataggio…" : "Reimposta password"}
          </button>

          <Link to="/">Torna al login</Link>
        </form>
      )}
    </RecoveryLayout>
  );
}
